import {
  Component,
  computed,
  ElementRef,
  input,
  OnChanges,
  OnInit,
  output,
  signal,
  SimpleChanges,
  viewChild
} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {NgStyle} from '@angular/common';

@Component({
  selector: 'app-dual-range',
  imports: [
    FormsModule,
    NgStyle
  ],
  templateUrl: './dual-range.html',
  styleUrl: './dual-range.css',
})
export class DualRange implements OnInit, OnChanges {
  min = input<number>(0);
  max = input<number>(100);
  step = input<number>(1);
  initialMin = input<null | number>(null);
  initialMax = input<null | number>(null);
  label = input<string>('Range');
  prefix = input<string>('');
  suffix = input<string>('');
  formatter = input<((value: number) => string) | null>(null);
  autoStep = input<boolean>(false);
  resetStrategy = input<'reset' | 'clamp' | 'preserve'>('reset');

  rangeChange = output<{ min: number; max: number }>();

  calculatedStep = computed(() => {
    return this.autoStep() ? this.calculateStep(this.min(), this.max()) : this.step();
  });
  // Bounds snapped to the step so the slider ends land on a step value
  rangeMin = computed(() => this.snapToStepDown(this.min(), this.calculatedStep()));
  rangeMax = computed(() => this.snapToStepUp(this.max(), this.calculatedStep()));

  minValue = signal(25);
  maxValue = signal(75);

  showingMinTooltip = signal(false);
  showingMaxTooltip = signal(false);
  private minTooltipTimeout?: ReturnType<typeof setTimeout>;
  private maxTooltipTimeout?: ReturnType<typeof setTimeout>;
  private hoverTimeout?: ReturnType<typeof setTimeout>;
  private isInitialized = false; // Track if we've done first init

  minInput = viewChild('minInput', { read: ElementRef });
  maxInput = viewChild('maxInput', { read: ElementRef });

  ngOnInit() {
    this.minValue.set(this.initialMin() ?? this.rangeMin());
    this.maxValue.set(this.initialMax() ?? this.rangeMax());
    this.validateRange();
    this.isInitialized = true;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.isInitialized) return;

    if (changes['min'] || changes['max']) {
      this.handleRangeChange();
    }
  }

  private handleRangeChange() {
    const min = this.rangeMin();
    const max = this.rangeMax();
    const clamp = (value: number) => Math.max(min, Math.min(value, max));

    switch (this.resetStrategy()) {
      case 'reset':
        // Full reset to bounds
        this.minValue.set(min);
        this.maxValue.set(max);
        break;

      case 'clamp':
        // Keep values but clamp to new range
        this.minValue.update(clamp);
        this.maxValue.update(clamp);
        break;

      case 'preserve':
        // Try to keep the same relative position in the range
        const oldRange = max - min;
        const newRange = max - min;

        if (oldRange > 0) {
          const minPercent = (this.minValue() - min) / oldRange;
          const maxPercent = (this.maxValue() - min) / oldRange;

          // Still clamp to bounds
          this.minValue.set(clamp(min + (minPercent * newRange)));
          this.maxValue.set(clamp(min + (maxPercent * newRange)));
        }
        break;
    }

    this.validateRange();
    this.emitChange(); // Let parent know values changed
  }

  private snapToStepDown(value: number, step: number): number {
    return Math.max(0, Math.floor(value / step) * step);
  }

  private snapToStepUp(value: number, step: number): number {
    return Math.ceil(value / step) * step;
  }

  calculateStep(min: number, max: number): number {
    const range = max - min;

    switch (true) {
      case range <= 100:
        return 10;
      case range <= 500:
        return 25;
      case range <= 1000:
        return 50;
      case range <= 5000:
        return 250;
      default:
        return 500;
    }
  }

  getMinTooltipStyle(): any {
    return this.getTooltipStyle(this.minInput(), this.getMinPercent(), this.showingMinTooltip());
  }

  getMaxTooltipStyle(): any {
    return this.getTooltipStyle(this.maxInput(), this.getMaxPercent(), this.showingMaxTooltip());
  }

  private getTooltipStyle(input: ElementRef | undefined, percent: number, visible: boolean): any {
    if (!input) return {};

    const inputRect = input.nativeElement.getBoundingClientRect();
    const thumbWidth = 20; // 5 * 4px (w-5 from Tailwind)
    const availableWidth = inputRect.width - thumbWidth;
    const thumbPosition = ((percent / 100) * availableWidth) + (thumbWidth / 2);

    return {
      display: visible ? 'block' : 'none',
      position: 'fixed',
      left: `${inputRect.left + thumbPosition}px`,
      top: `${inputRect.top - 32}px`,
      transform: 'translateX(-50%)'
    };
  }

  onMinChange(event: Event) {
    const value = +(event.target as HTMLInputElement).value;
    if (value <= this.maxValue() - this.step()) {
      this.minValue.set(value);
      this.emitChange();
    } else {
      (event.target as HTMLInputElement).value = this.minValue().toString();
    }
    this.showMinTooltip();
  }

  onMaxChange(event: Event) {
    const value = +(event.target as HTMLInputElement).value;
    if (value >= this.minValue() + this.step()) {
      this.maxValue.set(value);
      this.emitChange();
    } else {
      (event.target as HTMLInputElement).value = this.maxValue().toString();
    }
    this.showMaxTooltip();
  }

  showMinTooltip() {
    clearTimeout(this.hoverTimeout);

    this.hoverTimeout = setTimeout(() => {
      this.showingMinTooltip.set(true);
      clearTimeout(this.minTooltipTimeout);
      this.minTooltipTimeout = setTimeout(() => {
        this.showingMinTooltip.set(false);
      }, 1500);
    }, 200);
  }

  showMaxTooltip() {
    clearTimeout(this.hoverTimeout);

    this.hoverTimeout = setTimeout(() => {
      this.showingMaxTooltip.set(true);
      clearTimeout(this.maxTooltipTimeout);
      this.maxTooltipTimeout = setTimeout(() => {
        this.showingMaxTooltip.set(false);
      }, 1500);
    }, 200);
  }

  validateRange() {
    if (this.minValue() > this.maxValue()) {
      this.minValue.set(this.maxValue() - this.step());
    }
  }

  emitChange() {
    this.rangeChange.emit({min: this.minValue(), max: this.maxValue()});
  }

  getMinPercent(): number {
    return ((this.minValue() - this.rangeMin()) / (this.rangeMax() - this.rangeMin())) * 100;
  }

  getMaxPercent(): number {
    return ((this.maxValue() - this.rangeMin()) / (this.rangeMax() - this.rangeMin())) * 100;
  }

  getActiveWidth(): number {
    return ((this.maxValue() - this.minValue()) / (this.rangeMax() - this.rangeMin())) * 100;
  }

  formatValue(value: number): string {
    const formatter = this.formatter();
    if (formatter) return formatter(value);
    return `${this.prefix()}${Math.round(value)}${this.suffix()}`;
  }
}
